import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../Authentification/AuthContext";
import { createPostPlanApi } from "./PostPlanAPI";
import { PostPlan, PostPlanStatus } from "./PostPlanTypes";
import { USECASE_LABELS } from "./usecaseConfig";

interface PostPlanBrowserProps {
    onSelectPlan: (plan: PostPlan) => void;
}

const STATUS_LABELS: Record<PostPlanStatus, string> = {
    draft: "Entwurf",
    in_review: "Zur Prüfung",
    confirmed: "Bestätigt",
    posted: "Gepostet",
};

export default function PostPlanBrowser({ onSelectPlan }: PostPlanBrowserProps) {
    const { token, hasRole } = useAuth();
    const api = useMemo(() => createPostPlanApi(token), [token]);

    const [plans, setPlans] = useState<PostPlan[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isAdmin = hasRole("admin");
    const isAuthor = hasRole("author");
    const isModerator = hasRole("moderator");

   const loadPlans = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const results: PostPlan[] = [];

            const addUnique = (plans: PostPlan[]) => {
            for (const plan of plans) {
                if (!results.some((existing) => existing.id === plan.id)) {
                results.push(plan);
                }
            }
            };

            // Admin sieht alle Postpläne unabhängig vom Status
            if (isAdmin) {
            addUnique(await api.list());
            } else {
            // Autor sieht seine eigenen Pläne in jedem Status
            if (isAuthor) {
                addUnique(
                await api.list({
                    mine: true,
                })
                );
            }

            // Moderator sieht alle Pläne, für die Moderation relevant bleibt
            if (isModerator) {
                addUnique(
                await api.list({
                    status: "in_review",
                })
                );

                addUnique(
                await api.list({
                    status: "confirmed",
                })
                );

                addUnique(
                await api.list({
                    status: "posted",
                })
                );
            }
            }

            results.sort((a, b) =>
            b.updatedAt.localeCompare(a.updatedAt)
            );

            setPlans(results);
        } catch (err) {
            setError(
            err instanceof Error
                ? err.message
                : "Postpläne konnten nicht geladen werden"
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return (
        <div className="post-plan-browser">
            <div className="post-plan-browser-header">
            <div>
                <h2>Gespeicherte Postpläne</h2>
                <p>Öffne einen bestehenden Plan oder aktualisiere die Liste.</p>
            </div>

            <button
                type="button"
                className="btn-secondary"
                onClick={loadPlans}
                disabled={isLoading}
            >
                {isLoading ? "Lädt..." : "Aktualisieren"}
            </button>
            </div>

            {error && (
            <p className="post-plan-error">
                {error}
            </p>
            )}

            {!isLoading && plans.length === 0 && !error && (
            <div className="post-plan-browser-empty">
                Noch keine Postpläne vorhanden.
            </div>
            )}

            <div className="post-plan-browser-grid">
            {plans.map((plan) => (
                <article
                key={plan.id}
                className="post-plan-browser-card"
                >
                <div className="post-plan-browser-card__header">
                    <div>

                    <div className="post-plan-browser-card__title">
                    Postplan
                    </div>
                    
                    <div className="post-plan-browser-card__usecases">
                        {plan.usecases.length > 0 ? (
                            plan.usecases.map((usecase) => (
                            <span
                                key={usecase}
                                className="post-plan-browser-card__usecase"
                            >
                                {USECASE_LABELS[usecase] ?? usecase}
                            </span>
                            ))
                        ) : (
                            <span className="post-plan-browser-card__meta">
                            Kein Kanal hinterlegt
                            </span>
                        )}
                        </div>
                    <div className="post-plan-browser-card__meta">
                        {plan.entries.length}{" "}
                        {plan.entries.length === 1 ? "Beitrag" : "Beiträge"}
                    </div>
                    </div>

                    <span
                    className={`plan-status plan-status--${plan.status}`}
                    >
                    {STATUS_LABELS[plan.status]}
                    </span>
                </div>

                <div className="post-plan-browser-card__details">
                    <div>
                    <span className="post-plan-browser-card__label">
                        Autor
                    </span>
                    <span>{plan.authorUsername}</span>
                    </div>

                    {plan.moderatorUsername && (
                    <div>
                        <span className="post-plan-browser-card__label">
                        Geprüft von
                        </span>
                        <span>{plan.moderatorUsername}</span>
                    </div>
                    )}

                    <div>
                    <span className="post-plan-browser-card__label">
                        Aktualisiert
                    </span>
                    <span>
                        {new Date(plan.updatedAt).toLocaleString("de-DE")}
                    </span>
                    </div>
                </div>

                <div className="post-plan-browser-card__actions">
                    <button
                    type="button"
                    className="btn-primary"
                    onClick={() => onSelectPlan(plan)}
                    >
                    Plan öffnen
                    </button>
                </div>
                </article>
            ))}
            </div>
        </div>
    );
}